import { useState, useRef, useEffect } from 'react'
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Thermometer, Droplets, Wind } from 'lucide-react'
import { useWeatherStore } from '@/stores/weatherStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { Tooltip } from '@/components/ui/Tooltip'
import type { WeatherCondition } from '@/services/weatherService'

const conditionIcons: Record<WeatherCondition, React.FC<{ className?: string }>> = {
  clear: Sun,
  cloudy: Cloud,
  rain: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
}

const conditionLabels: Record<WeatherCondition, string> = {
  clear: '맑음',
  cloudy: '흐림',
  rain: '비',
  snow: '눈',
  storm: '뇌우',
}

export function WeatherIndicator() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const weatherEnabled = useSettingsStore((s) => s.settings.weatherThemeEnabled)
  const weatherData = useWeatherStore((s) => s.weatherData)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!weatherEnabled || !weatherData) return null

  const Icon = conditionIcons[weatherData.condition]
  const label = conditionLabels[weatherData.condition]
  const temp = Math.round(weatherData.temperature)
  const apparentTemp = weatherData.apparentTemperature !== undefined ? Math.round(weatherData.apparentTemperature) : temp
  const humidity = weatherData.humidity !== undefined ? Math.round(weatherData.humidity) : '--'
  const windSpeed = weatherData.windSpeed !== undefined ? weatherData.windSpeed.toFixed(1) : '--'

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip content={isOpen ? '' : '날씨 상세 정보 보기'} placement="bottom">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-caption transition-colors ${
            isOpen ? 'bg-surface-tertiary text-heading' : 'hover-surface'
          }`}
        >
          <Icon className="w-4 h-4" />
          <span className="text-xs font-medium tabular-nums">{temp}°</span>
        </button>
      </Tooltip>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-56 p-4 rounded-xl shadow-lg border border-base bg-surface-elevated z-50 animate-in fade-in slide-in-from-top-2">
          {/* Header (Main Icon & Temp) */}
          <div className="flex items-center gap-4 border-b border-base pb-3">
            <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-500 shadow-sm border border-primary-100 dark:bg-primary-900/30 dark:border-primary-800">
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-heading tabular-nums leading-none">
                {temp}°
              </div>
              <div className="text-sm font-medium text-body mt-1">
                {label}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="mt-3 space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-caption flex items-center gap-1.5">
                <Thermometer className="w-4 h-4" />
                체감 온도
              </span>
              <span className="font-medium text-heading tabular-nums">{apparentTemp}°C</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-caption flex items-center gap-1.5">
                <Droplets className="w-4 h-4" />
                습도
              </span>
              <span className="font-medium text-heading tabular-nums">{humidity}%</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-caption flex items-center gap-1.5">
                <Wind className="w-4 h-4" />
                풍속
              </span>
              <span className="font-medium text-heading tabular-nums">{windSpeed} km/h</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
